/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */


const MILLIS_PER_MINUTE = 60 * 1000;
const MILLIS_PER_HOUR = 60 * MILLIS_PER_MINUTE;


/*******************************************************************************
 ** midnight (local time) at the start of the calendar day containing the time.
 *******************************************************************************/
function startOfDay(time: number): Date
{
   const date = new Date(time);
   return (new Date(date.getFullYear(), date.getMonth(), date.getDate()));
}


/*******************************************************************************
 ** whole calendar days between the day of lastUsedAt and the day of now.  Uses
 ** the date constructor rather than dividing millis, so daylight saving changes
 ** don't skew it.
 *******************************************************************************/
function calendarDaysAgo(lastUsedAt: number, now: number): number
{
   const lastDay = startOfDay(lastUsedAt);
   const today = startOfDay(now);
   return (Math.round((today.getTime() - lastDay.getTime()) / (24 * MILLIS_PER_HOUR)));
}


/*******************************************************************************
 ** "Today", "Yesterday", or the weekday and date, such as "Sunday 10/18/2026".
 *******************************************************************************/
export function formatQueryHistoryGroupLabel(lastUsedAt: number, now: number): string
{
   const daysAgo = calendarDaysAgo(lastUsedAt, now);
   if (daysAgo <= 0)
   {
      return ("Today");
   }
   if (daysAgo === 1)
   {
      return ("Yesterday");
   }

   const date = new Date(lastUsedAt);
   return (`${date.toLocaleDateString([], {weekday: "long"})} ${date.toLocaleDateString([], {year: "numeric", month: "numeric", day: "numeric"})}`);
}


/*******************************************************************************
 ** for something used today, how long ago; for anything older, the time of day.
 *******************************************************************************/
export function formatQueryHistoryTimeLabel(lastUsedAt: number, now: number): string
{
   if (calendarDaysAgo(lastUsedAt, now) > 0)
   {
      return (new Date(lastUsedAt).toLocaleTimeString([], {hour: "numeric", minute: "2-digit"}));
   }

   const elapsed = now - lastUsedAt;
   if (elapsed < MILLIS_PER_MINUTE)
   {
      return ("just now");
   }

   if (elapsed < MILLIS_PER_HOUR)
   {
      const minutes = Math.floor(elapsed / MILLIS_PER_MINUTE);
      return (`${minutes} min ago`);
   }

   const hours = Math.floor(elapsed / MILLIS_PER_HOUR);
   return (`${hours} ${hours === 1 ? "hour" : "hours"} ago`);
}


/*******************************************************************************
 ** both exact times, one per line (the tooltip showing this preserves the
 ** line break).
 *******************************************************************************/
export function formatQueryHistoryTooltip(firstUsedAt: number, lastUsedAt: number): string
{
   return (`Last used ${new Date(lastUsedAt).toLocaleString()}\nFirst used ${new Date(firstUsedAt).toLocaleString()}`);
}
